import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from '../../components/ui/button';

const DISMISS_KEY = 'mybookie_pwa_dismiss';
const DISMISS_DAYS = 7;

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsStandalone(standalone);
    if (standalone) return;

    // Check dismissal
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) {
      const dismissDate = new Date(dismissed);
      const daysSince = (Date.now() - dismissDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSince < DISMISS_DAYS) return;
    }

    // Detect iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    // Listen for beforeinstallprompt (Android/Chrome/Edge)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show after 30 seconds of engagement
      setTimeout(() => setShowBanner(true), 30000);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // For iOS, show after 30 seconds
    if (ios) {
      setTimeout(() => setShowBanner(true), 30000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      if (outcome === 'accepted') {
        setShowBanner(false);
      }
    }
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    setShowBanner(false);
    localStorage.setItem(DISMISS_KEY, new Date().toISOString());
  }, []);

  if (isStandalone || !showBanner) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 animate-in slide-in-from-bottom duration-500"
      data-testid="pwa-install-banner"
    >
      <div className="max-w-lg mx-auto p-3">
        <div className="bg-[#0A2B1D] text-white rounded-xl shadow-2xl p-4 flex items-center gap-3 border border-[#3A6E53]">
          <div className="w-11 h-11 rounded-xl bg-[#1A3D2E] flex items-center justify-center shrink-0">
            <Smartphone className="w-5 h-5 text-[#9CC4AF]" />
          </div>
          
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">Get MyBookie on your phone</p>
            {isIOS ? (
              <p className="text-xs text-[#9CC4AF] mt-0.5">
                Tap <span className="inline-flex items-center"><svg className="w-3 h-3 mx-0.5 inline" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L12 16M12 2L7 7M12 2L17 7"/><path d="M4 14v5a2 2 0 002 2h12a2 2 0 002-2v-5" stroke="currentColor" strokeWidth="2" fill="none"/></svg></span> then "Add to Home Screen"
              </p>
            ) : (
              <p className="text-xs text-[#9CC4AF] mt-0.5">Quick access from your home screen</p>
            )}
          </div>

          {!isIOS && deferredPrompt && (
            <Button 
              onClick={handleInstall}
              size="sm"
              className="bg-white text-[#0A2B1D] hover:bg-[#E2ECE7] font-semibold text-xs shrink-0 h-8 px-3"
              data-testid="pwa-install-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1" />
              Install
            </Button>
          )}

          <button 
            onClick={handleDismiss}
            className="text-[#6BA38A] hover:text-white p-1 shrink-0"
            data-testid="pwa-dismiss-btn"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
